import React from 'react';
import { noOutlineHandlers } from '@/utils/common/modal/modalUtils';

interface DeleteConfirmModalProps {
  showDeleteConfirm: boolean;
  isRequestingDelete: boolean;
  isAdmin: boolean;
  hasDeleteRequest: boolean;
  onClose: () => void;
  onConfirm: () => void;
  getDeleteConfirmationMessage: (isAdmin: boolean, hasDeleteRequest: boolean) => string;
}

const DeleteConfirmModal: React.FC<DeleteConfirmModalProps> = ({
  showDeleteConfirm,
  isRequestingDelete,
  isAdmin,
  hasDeleteRequest,
  onClose,
  onConfirm,
  getDeleteConfirmationMessage,
}) => {
  if (!showDeleteConfirm) return null;

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/prefer-tag-over-role
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 z-[60] flex items-center justify-center p-4"
      onClick={onClose}
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          onClose();
        }
      }}
      role="button"
      tabIndex={0}
      aria-label="Close delete confirmation"
    >
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/no-static-element-interactions, jsx-a11y/prefer-tag-over-role */}
      <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        {/* Modal Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h3 className="text-xl font-semibold text-gray-900" style={{ fontFamily: 'Maven Pro' }}>
            Delete Request
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            style={{ outline: 'none', boxShadow: 'none', border: 'none', background: 'transparent' }}
            disabled={isRequestingDelete}
            {...noOutlineHandlers}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Modal Content */}
        <div className="px-6 py-8 text-center">
          {/* Delete Icon */}
          <div className="mx-auto w-20 h-20 rounded-full flex items-center justify-center mb-6" style={{ backgroundColor: '#fee2e2' }}>
            <svg className="w-10 h-10" fill="none" stroke="#ef4444" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </div>

          {/* Confirmation Text */}
          <p className="text-gray-700 text-base mb-8" style={{ fontFamily: 'Maven Pro', fontSize: '16px' }}>
            {getDeleteConfirmationMessage(isAdmin, hasDeleteRequest)}
          </p>

          {/* Action Buttons */}
          <div className="flex gap-4">
            <button
              onClick={onClose}
              className="flex-1 px-6 py-3 text-gray-700 bg-white border-2 border-gray-300 rounded-full hover:bg-gray-50 transition-colors"
              style={{ 
                fontFamily: 'Maven Pro', 
                fontSize: '16px', 
                fontWeight: 500,
                outline: 'none !important',
                boxShadow: 'none !important',
                WebkitTapHighlightColor: 'transparent'
              }}
              disabled={isRequestingDelete}
              {...noOutlineHandlers}
            >
              {!isAdmin && hasDeleteRequest ? 'OK' : 'Cancel'}
            </button>
            {(!hasDeleteRequest || isAdmin) && (
              <button
                onClick={onConfirm}
                className="flex-1 px-6 py-3 text-white rounded-full hover:bg-red-700 transition-colors disabled:opacity-50"
                style={{ 
                  backgroundColor: '#ef4444', 
                  fontFamily: 'Maven Pro', 
                  fontSize: '16px', 
                  fontWeight: 500,
                  outline: 'none !important',
                  boxShadow: 'none !important',
                  border: 'none',
                  WebkitTapHighlightColor: 'transparent'
                }}
                disabled={isRequestingDelete}
                {...noOutlineHandlers}
              >
                {(() => {
                  if (isRequestingDelete) {
                    return isAdmin ? 'Deleting...' : 'Submitting...';
                  }
                  return isAdmin ? 'Delete' : 'Submit';
                })()}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeleteConfirmModal;
